import { Modal, Form, Typography, Select, Button, DatePicker, useSelect, Space, Table } from "@pankod/refine-antd";
import { useTranslate } from "@pankod/refine-core";
import { IRoom } from "interfaces";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useAuthenticatedRequest } from 'core/useAuthenticatedRequest';

import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import 'dayjs/locale/fr'
dayjs.extend(localizedFormat)

const { Title, Text } = Typography;

interface PreviewBooking {
  booking_id: number;
  client_name: string;
  client_email: string;
  start: string;
  end: string;
}

interface PreviewResult {
  count: number;
  bookings: PreviewBooking[];
}

interface BulkSendResult {
  sent: number;
  errors: number;
  booking_ids_sent: number[];
  booking_ids_error: number[];
}

type ModalStep = 'form' | 'preview' | 'result';

export const BulkSendMailModal = forwardRef((props, ref) => {
  const t = useTranslate();
  const { sendRequest, isLoading } = useAuthenticatedRequest();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<ModalStep>('form');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [result, setResult] = useState<BulkSendResult | null>(null);
  const [formValues, setFormValues] = useState<{ room_id: number; date_start: string; date_end: string } | null>(null);
  const [form] = Form.useForm();

  const { selectProps: roomSelectProps } = useSelect<IRoom>({
    resource: "rooms",
    optionValue: "id",
    optionLabel: "name",
    fetchSize: 100,
  });

  useImperativeHandle(ref, () => ({
    show() {
      setVisible(true);
      setStep('form');
      setPreview(null);
      setResult(null);
      setFormValues(null);
      form.resetFields();
    }
  }));

  const handleClose = () => {
    setVisible(false);
    setStep('form');
    setPreview(null);
    setResult(null);
    setFormValues(null);
    form.resetFields();
  };

  const handlePreview = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        room_id: values.room_id,
        date_start: dayjs(values.date_range[0]).format('YYYY-MM-DD'),
        date_end: dayjs(values.date_range[1]).format('YYYY-MM-DD'),
      };
      setFormValues(payload);

      const response = await sendRequest({
        url: 'bookings/bulk_send_book_mail_preview',
        method: 'post',
        data: payload
      });
      setPreview(response);
      setStep('preview');
    } catch (error) {
      console.error('Erreur lors de la prévisualisation:', error);
    }
  };

  const handleSend = async () => {
    if (!formValues) return;

    try {
      const response = await sendRequest({
        url: 'bookings/bulk_send_book_mail',
        method: 'post',
        data: formValues
      });
      setResult(response);
      setStep('result');
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
    }
  };

  const handleBack = () => {
    setStep('form');
    setPreview(null);
  };

  const previewColumns = [
    {
      title: 'ID',
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: 70,
    },
    {
      title: t("pages.booking.client"),
      dataIndex: 'client_name',
      key: 'client_name',
    },
    {
      title: t("pages.booking.email"),
      dataIndex: 'client_email',
      key: 'client_email',
    },
    {
      title: t("pages.booking.start"),
      dataIndex: 'start',
      key: 'start',
    },
    {
      title: t("pages.booking.end"),
      dataIndex: 'end',
      key: 'end',
    },
  ];

  const getFooter = () => {
    if (step === 'form') {
      return [
        <Button key="cancel" onClick={handleClose}>
          {t("buttons.cancel")}
        </Button>,
        <Button key="preview" type="primary" loading={isLoading} onClick={handlePreview}>
          {t("pages.booking.preview")}
        </Button>
      ];
    }

    if (step === 'preview') {
      return [
        <Button key="back" onClick={handleBack}>
          {t("pages.booking.back")}
        </Button>,
        <Button
          key="send"
          type="primary"
          danger
          loading={isLoading}
          onClick={handleSend}
          disabled={!preview || preview.count === 0}
        >
          {t("pages.booking.confirm_send")} ({preview?.count || 0})
        </Button>
      ];
    }

    return [
      <Button key="close" onClick={handleClose}>
        {t("buttons.close")}
      </Button>
    ];
  };

  return (
    <Modal
      title={<Title level={4}>{t("pages.booking.bulk_send_mail_title")}</Title>}
      visible={visible}
      onCancel={handleClose}
      footer={getFooter()}
      width={step === 'preview' ? 800 : 520}
    >
      {step === 'form' && (
        <Form form={form} layout="vertical">
          <Form.Item
            name="room_id"
            label={t("pages.booking.room")}
            rules={[{ required: true, message: t("errors.required") }]}
          >
            <Select
              {...roomSelectProps}
              placeholder={t("pages.booking.select_room")}
            />
          </Form.Item>

          <Form.Item
            name="date_range"
            label={t("pages.booking.date_range")}
            rules={[{ required: true, message: t("errors.required") }]}
          >
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </Form.Item>

          <Text type="secondary">
            {t("pages.booking.bulk_send_mail_info")}
          </Text>
        </Form>
      )}

      {step === 'preview' && preview && (
        <div>
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4 }}>
            <Text strong style={{ color: '#d48806' }}>
              {t("pages.booking.preview_warning", { count: preview.count })}
            </Text>
          </div>

          {preview.count > 0 ? (
            <Table
              dataSource={preview.bookings}
              columns={previewColumns}
              rowKey="booking_id"
              pagination={preview.count > 10 ? { pageSize: 10 } : false}
              size="small"
              scroll={{ y: 300 }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Text type="secondary">{t("pages.booking.no_bookings_found")}</Text>
            </div>
          )}
        </div>
      )}

      {step === 'result' && result && (
        <div>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{
              padding: '16px',
              backgroundColor: result.sent > 0 ? '#f6ffed' : '#fff7e6',
              borderRadius: '8px',
              border: `1px solid ${result.sent > 0 ? '#b7eb8f' : '#ffd591'}`
            }}>
              <Title level={5} style={{ margin: 0, color: result.sent > 0 ? '#52c41a' : '#fa8c16' }}>
                {t("pages.booking.bulk_send_result")}
              </Title>
              <div style={{ marginTop: '8px' }}>
                <Text strong style={{ color: '#52c41a' }}>
                  {result.sent} {t("pages.booking.mails_sent")}
                </Text>
                {result.errors > 0 && (
                  <Text strong style={{ color: '#ff4d4f', marginLeft: '16px' }}>
                    {result.errors} {t("pages.booking.mails_errors")}
                  </Text>
                )}
              </div>
            </div>

            {result.booking_ids_sent.length > 0 && (
              <div>
                <Text type="secondary">
                  {t("pages.booking.bookings_sent")}: {result.booking_ids_sent.join(', ')}
                </Text>
              </div>
            )}

            {result.booking_ids_error.length > 0 && (
              <div>
                <Text type="danger">
                  {t("pages.booking.bookings_error")}: {result.booking_ids_error.join(', ')}
                </Text>
              </div>
            )}
          </Space>
        </div>
      )}
    </Modal>
  );
});
